import React, { useEffect, useState, useCallback } from 'react';
import { Navbar } from '../components/Navbar';
import { 
  Plus, Edit2, Trash2, Package, AlertCircle, Check, X, 
  Upload, Image, Loader, ChevronDown, Eye, EyeOff, Star, ChevronLeft, ChevronRight
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';
import { ImageCarousel } from '../components/ImageCarousel';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MAX_IMAGES = 8;
const MIN_IMAGES = 1;

export const SellerProducts = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState({});
  const [sellerCategories, setSellerCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [mainImageIndex, setMainImageIndex] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    original_price: '',
    discounted_price: '',
    category: '',
    seller_category_id: '',
    product_link: '',
    tracking_link: '',
    image_url: '',
    images: []
  });

  const fetchProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/seller/products`, { withCredentials: true });
      setProducts(response.data);
    } catch (error) {
      toast.error('Erro ao carregar produtos');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/marketplace/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  }, []);

  const fetchSellerCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/seller/categories`, { withCredentials: true });
      setSellerCategories(response.data.categories || []);
    } catch (error) {
      console.error('Erro ao carregar categorias do vendedor:', error);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchSellerCategories();
  }, [fetchProducts, fetchCategories, fetchSellerCategories]);

  // Validação em tempo real (debounced)
  const validateProduct = useCallback(async () => {
    if (!formData.name || !formData.description || !formData.category) {
      setValidationResult(null);
      return;
    }

    setValidating(true);
    try {
      const response = await axios.post(
        `${API_URL}/marketplace/products/validate`,
        {
          name: formData.name,
          description: formData.description,
          original_price: parseFloat(formData.original_price) || 0,
          discounted_price: parseFloat(formData.discounted_price) || 0,
          category: formData.category,
          image_url: formData.image_url,
          images: formData.images
        },
        { withCredentials: true }
      );
      setValidationResult(response.data);
    } catch (error) {
      console.error('Erro na validação:', error);
    } finally {
      setValidating(false);
    }
  }, [formData]);

  // Debounce validation
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.name.length >= 5 && formData.description.length >= 20) {
        validateProduct();
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.name, formData.description, formData.category, formData.images, validateProduct]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar número de imagens
    if (formData.images.length < MIN_IMAGES) {
      toast.error(`Mínimo de ${MIN_IMAGES} imagem obrigatória`);
      return;
    }
    if (formData.images.length > MAX_IMAGES) {
      toast.error(`Máximo de ${MAX_IMAGES} imagens permitidas`);
      return;
    }

    // Reorganizar imagens para que a principal seja a primeira
    let orderedImages = [...formData.images];
    if (mainImageIndex > 0 && mainImageIndex < orderedImages.length) {
      const mainImage = orderedImages[mainImageIndex];
      orderedImages.splice(mainImageIndex, 1);
      orderedImages.unshift(mainImage);
    }
    
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        original_price: parseFloat(formData.original_price),
        discounted_price: parseFloat(formData.discounted_price),
        category: formData.category,
        seller_category_id: formData.seller_category_id || null,
        product_link: formData.product_link || null,
        tracking_link: formData.tracking_link || null,
        image_url: orderedImages[0] || null,  // Primeira imagem é a principal
        images: orderedImages
      };

      let response;
      if (editingProduct) {
        response = await axios.put(
          `${API_URL}/marketplace/products/${editingProduct.product_id}`,
          payload,
          { withCredentials: true }
        );
      } else {
        response = await axios.post(
          `${API_URL}/marketplace/products`,
          payload,
          { withCredentials: true }
        );
      }

      if (response.data.success) {
        toast.success(response.data.message || 'Produto salvo!');
        
        if (response.data.melhorias?.length > 0) {
          toast.info(`Melhorias aplicadas: ${response.data.melhorias.join(', ')}`);
        }
        
        if (response.data.avisos?.length > 0) {
          response.data.avisos.forEach(aviso => toast.warning(aviso));
        }

        resetForm();
        fetchProducts();
      } else {
        // Produto bloqueado
        toast.error('Produto não aprovado. Corrija os erros abaixo.');
        setValidationResult({
          status: 'bloqueado',
          erros: response.data.erros,
          avisos: response.data.avisos
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar produto');
    }
  };

  const resetForm = () => {
    setIsDialogOpen(false);
    setEditingProduct(null);
    setValidationResult(null);
    setMainImageIndex(0);
    setFormData({
      name: '',
      description: '',
      original_price: '',
      discounted_price: '',
      category: '',
      seller_category_id: '',
      product_link: '',
      tracking_link: '',
      image_url: '',
      images: []
    });
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setMainImageIndex(0);
    setFormData({
      name: product.name,
      description: product.description,
      original_price: product.original_price,
      discounted_price: product.discounted_price,
      category: product.category || '',
      seller_category_id: product.seller_category_id || '',
      product_link: product.product_link || '',
      tracking_link: product.tracking_link || '',
      image_url: product.image_url || '',
      images: product.images || (product.image_url ? [product.image_url] : [])
    });
    setValidationResult(null);
    setIsDialogOpen(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Tem certeza que deseja excluir este produto?')) return;
    
    try {
      await axios.delete(`${API_URL}/marketplace/products/${productId}`, { withCredentials: true });
      toast.success('Produto excluído!');
      fetchProducts();
    } catch (error) {
      toast.error('Erro ao excluir produto');
    }
  };

  const handleToggleActive = async (product) => {
    try {
      await axios.put(
        `${API_URL}/marketplace/products/${product.product_id}`,
        { active: !product.active },
        { withCredentials: true }
      );
      toast.success(product.active ? 'Produto desativado' : 'Produto ativado');
      fetchProducts();
    } catch (error) {
      toast.error('Erro ao alterar status');
    }
  };

  // Upload de imagem
  const handleImageUpload = async (e, isGallery = false) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Verificar limite de imagens
    const currentCount = formData.images.length;
    const maxToAdd = MAX_IMAGES - currentCount;
    
    if (maxToAdd <= 0) {
      toast.error(`Máximo de ${MAX_IMAGES} imagens atingido`);
      return;
    }

    const filesToUpload = files.slice(0, maxToAdd);
    if (files.length > maxToAdd) {
      toast.warning(`Apenas ${maxToAdd} imagem(ns) será(ão) adicionada(s). Limite: ${MAX_IMAGES}`);
    }

    setUploading(true);
    const formDataUpload = new FormData();
    
    filesToUpload.forEach(file => formDataUpload.append('files', file));

    try {
      const response = await axios.post(
        `${API_URL}/upload/multiple`,
        formDataUpload,
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      if (response.data.uploaded) {
        const newUrls = response.data.uploaded.map(f => `${API_URL}/files/${f.file_id}`);
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, ...newUrls],
          image_url: prev.images.length === 0 ? newUrls[0] : prev.image_url
        }));
        toast.success(`${response.data.uploaded.length} imagem(ns) enviada(s)!`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar imagem');
    } finally {
      setUploading(false);
    }
  };

  const removeGalleryImage = (index) => {
    if (formData.images.length <= MIN_IMAGES) {
      toast.error(`Mínimo de ${MIN_IMAGES} imagem obrigatória`);
      return;
    }
    
    setFormData(prev => {
      const newImages = prev.images.filter((_, i) => i !== index);
      return {
        ...prev,
        images: newImages,
        image_url: newImages[0] || ''
      };
    });
    
    // Ajustar índice da imagem principal se necessário
    if (index === mainImageIndex) {
      setMainImageIndex(0);
    } else if (index < mainImageIndex) {
      setMainImageIndex(prev => prev - 1);
    }
  };

  const setMainImage = (index) => {
    setMainImageIndex(index);
    toast.success('Imagem principal definida!');
  };

  const getStatusBadge = (product) => {
    if (product.status === 'bloqueado') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
          <X className="w-3 h-3 mr-1" />
          Bloqueado
        </span>
      );
    }
    if (!product.active) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
          <EyeOff className="w-3 h-3 mr-1" />
          Inativo
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
        <Check className="w-3 h-3 mr-1" />
        Publicado
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2" data-testid="seller-products-title">
              Meus Produtos
            </h1>
            <p className="text-lg text-gray-600">
              Gerencie os produtos da sua loja
            </p>
          </div>
          <Button
            onClick={() => {
              resetForm();
              setIsDialogOpen(true);
            }}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
            data-testid="add-product-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo Produto
          </Button>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-8">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Sistema de Aprovação Automática</p>
              <p>Seus produtos são validados automaticamente. Se atender aos requisitos, será publicado imediatamente. Caso contrário, você verá os erros para corrigir.</p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader className="w-12 h-12 animate-spin text-indigo-600" />
          </div>
        ) : products.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Nenhum produto ainda</h3>
            <p className="text-gray-600 mb-6">Comece adicionando seu primeiro produto!</p>
            <Button
              onClick={() => setIsDialogOpen(true)}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Produto
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((product) => (
              <div
                key={product.product_id}
                className={`bg-white rounded-xl shadow-sm border overflow-hidden hover:shadow-md transition-shadow ${
                  product.status === 'bloqueado' ? 'border-red-200' : 
                  !product.active ? 'border-gray-200 opacity-75' : 'border-gray-200'
                }`}
                data-testid={`product-item-${product.product_id}`}
              >
                {product.image_url ? (
                  <div className="aspect-video w-full overflow-hidden bg-gray-100">
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="aspect-video w-full bg-gray-100 flex items-center justify-center">
                    <Package className="w-12 h-12 text-gray-400" />
                  </div>
                )}
                
                <div className="p-6">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 line-clamp-1">
                      {product.name}
                    </h3>
                    {getStatusBadge(product)}
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                    {product.description}
                  </p>

                  {product.category && categories.labels && (
                    <p className="text-xs text-indigo-600 font-medium mb-3">
                      {categories.labels[product.category]}
                    </p>
                  )}
                  
                  <div className="flex items-baseline space-x-2 mb-4">
                    {product.original_price > product.discounted_price && (
                      <span className="text-sm text-gray-500 line-through">
                        R$ {product.original_price.toFixed(2)}
                      </span>
                    )}
                    <span className="text-xl font-bold text-indigo-600">
                      R$ {product.discounted_price.toFixed(2)}
                    </span>
                  </div>

                  {/* Erros de validação */}
                  {product.validation_errors?.length > 0 && (
                    <div className="mb-4 p-2 bg-red-50 rounded-lg">
                      <p className="text-xs text-red-700 font-medium mb-1">Erros:</p>
                      <ul className="text-xs text-red-600">
                        {product.validation_errors.map((err, i) => (
                          <li key={i}>• {err}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(product)}
                      className="flex-1"
                      data-testid={`edit-product-${product.product_id}`}
                    >
                      <Edit2 className="w-4 h-4 mr-1" />
                      Editar
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleActive(product)}
                      title={product.active ? 'Desativar' : 'Ativar'}
                    >
                      {product.active ? (
                        <EyeOff className="w-4 h-4 text-gray-500" />
                      ) : (
                        <Eye className="w-4 h-4 text-green-600" />
                      )}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDelete(product.product_id)}
                      data-testid={`delete-product-${product.product_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Product Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="product-dialog-title">
              {editingProduct ? 'Editar Produto' : 'Novo Produto'}
            </DialogTitle>
            <DialogDescription>
              Preencha os dados do produto. Ele será validado automaticamente.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Validation Status */}
            {validationResult && (
              <div className={`p-4 rounded-lg ${
                validationResult.status === 'aprovado' 
                  ? 'bg-green-50 border border-green-200' 
                  : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  {validationResult.status === 'aprovado' ? (
                    <>
                      <Check className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-green-700">Produto aprovado!</span>
                    </>
                  ) : (
                    <>
                      <X className="w-5 h-5 text-red-600" />
                      <span className="font-medium text-red-700">Corrija os erros:</span>
                    </>
                  )}
                  {validating && <Loader className="w-4 h-4 animate-spin text-gray-500" />}
                </div>
                
                {validationResult.erros?.length > 0 && (
                  <ul className="text-sm text-red-700 space-y-1">
                    {validationResult.erros.map((erro, i) => (
                      <li key={i}>• {erro}</li>
                    ))}
                  </ul>
                )}
                
                {validationResult.avisos?.length > 0 && (
                  <ul className="text-sm text-amber-700 mt-2">
                    {validationResult.avisos.map((aviso, i) => (
                      <li key={i}>⚠ {aviso}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Título do Produto *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Curso Completo de Marketing Digital"
                  required
                  data-testid="product-name-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Mínimo 10 caracteres. Evite excesso de maiúsculas.
                </p>
              </div>
              
              <div className="md:col-span-2">
                <Label>Descrição *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  placeholder="Descreva seu produto em detalhes..."
                  required
                  data-testid="product-description-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {formData.description.length}/80 caracteres mínimos
                </p>
              </div>

              <div>
                <Label>Categoria Global *</Label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full h-10 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  required
                  data-testid="product-category-select"
                >
                  <option value="">Selecione...</option>
                  {categories.categories?.map(cat => (
                    <option key={cat} value={cat}>
                      {categories.labels?.[cat]}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label>Minha Categoria</Label>
                <select
                  value={formData.seller_category_id}
                  onChange={(e) => setFormData({ ...formData, seller_category_id: e.target.value })}
                  className="w-full h-10 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  data-testid="product-seller-category-select"
                >
                  <option value="">Sem categoria</option>
                  {sellerCategories.map(cat => (
                    <option key={cat.category_id} value={cat.category_id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Organize na sua loja
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Preço Original (R$)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.original_price}
                    onChange={(e) => setFormData({ ...formData, original_price: e.target.value })}
                    required
                    data-testid="product-original-price-input"
                  />
                </div>
                <div>
                  <Label>Preço com Desconto (R$)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.discounted_price}
                    onChange={(e) => setFormData({ ...formData, discounted_price: e.target.value })}
                    required
                    data-testid="product-discounted-price-input"
                  />
                </div>
              </div>

              <div className="md:col-span-2">
                <Label className="flex items-center gap-2">
                  Galeria de Imagens *
                  <span className="text-xs font-normal text-gray-500">
                    ({formData.images.length}/{MAX_IMAGES} imagens • Mínimo {MIN_IMAGES})
                  </span>
                </Label>
                <div className="space-y-3">
                  {formData.images.length > 0 && (
                    <ImageCarousel
                      images={formData.images}
                      mainImageIndex={mainImageIndex}
                      onSetMain={setMainImage}
                      onRemove={removeGalleryImage}
                      editable={true}
                      className="max-w-md"
                    />
                  )}
                  
                  {formData.images.length < MAX_IMAGES && (
                    <label className="block">
                      <div className="flex items-center justify-center h-16 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-indigo-500">
                        {uploading ? (
                          <Loader className="w-5 h-5 animate-spin text-indigo-600" />
                        ) : (
                          <div className="text-center">
                            <Image className="w-6 h-6 mx-auto text-gray-400 mb-1" />
                            <span className="text-sm text-gray-600">
                              {formData.images.length === 0 ? 'Adicionar imagens' : 'Adicionar mais imagens'}
                            </span>
                          </div>
                        )}
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={(e) => handleImageUpload(e, true)}
                        disabled={uploading}
                        data-testid="product-gallery-upload"
                      />
                    </label>
                  )}
                  
                  <p className="text-xs text-gray-500">
                    <Star className="w-3 h-3 inline mr-1 text-amber-500" />
                    Clique na estrela para definir a imagem principal (capa)
                  </p>
                </div>
              </div>

              <div>
                <Label>Link do Produto (opcional)</Label>
                <Input
                  type="url"
                  value={formData.product_link}
                  onChange={(e) => setFormData({ ...formData, product_link: e.target.value })}
                  placeholder="https://..."
                  data-testid="product-link-input"
                />
              </div>

              <div>
                <Label>Link de Rastreio (opcional)</Label>
                <Input
                  type="url"
                  value={formData.tracking_link}
                  onChange={(e) => setFormData({ ...formData, tracking_link: e.target.value })}
                  placeholder="https://..."
                  data-testid="product-tracking-input"
                />
              </div>
            </div>

            <div className="flex space-x-2 pt-4">
              <Button 
                type="submit" 
                className="flex-1" 
                disabled={uploading || (validationResult?.status === 'bloqueado')}
                data-testid="product-submit-btn"
              >
                {editingProduct ? 'Atualizar' : 'Publicar'} Produto
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={resetForm}
                data-testid="product-cancel-btn"
              >
                Cancelar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
